import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTheme, createUseStyles, Loader, Icon, Text, Select } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from 'lib/Web5Context';
import UploadImageCarousel from '../../../components/UploadImageCarousel';
import {
    getImages,
    deleteImage,
    getUploadImageUrl,
    uploadImage,
} from '../../../lib/services/forms';

import styles from './styles';
const useStyles = createUseStyles(styles, 'FormsModal');

function FormModal({
    formTemplates,
    form,
    className,
    carouselFullModalClass,
    showImages,
    expandImages,
    setExpandImages,
    isLoading,
    ...props
}) {
    const theme = useTheme();
    const classes = useStyles();
    const { t, bridgeBackend } = useWeb5Context();
    const [template, setTemplate] = useState(null);
    const [formTypes, setFormTypes] = useState(null);
    const [images, setImages] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    // Classes
    const rootClassNames = classnames(classes.root, className);
    const carouselFullModalClassNames = classnames(
        classes.carouselFullModal,
        carouselFullModalClass,
    );

    const getImagesList = useCallback(() => {
        form?.id &&
            getImages(form.id)
                .then((res) => setImages(res.data))
                .catch((err) => {
                    console.warn(err);
                });
        !form?.id && setImages([]);
    }, [form]);

    const onAddImage = useCallback(
        (droppedImages) => {
            const uploadPromises = [];
            const newImages = [];
            droppedImages.forEach((image) => {
                let imageId;
                return uploadPromises.push(
                    form?.id &&
                        getUploadImageUrl(form.id, { name: image.name })
                            .then((res) => {
                                imageId = res.data.id;
                                return uploadImage(res.data.locator, image);
                            })
                            .then((res) => {
                                if (res.status === 200) {
                                    newImages.push({
                                        id: imageId,
                                        description: image.name,
                                        strLink: URL.createObjectURL(image),
                                    });
                                } else {
                                    console.warn('Upload error');
                                }
                            })
                            .catch(console.warn),
                );
            });
            Promise.all(uploadPromises)
                .then(() => {
                    setImages([...images, ...newImages]);
                })
                .catch((err) => {
                    console.warn(err);
                });
        },
        [form, images],
    );

    const onDeleteImage = useCallback(
        (index) => {
            deleteImage(images[index].id).then((res) => {
                if (res.status === 200) {
                    const filteredImages = [...images];
                    filteredImages.splice(index, 1);
                    setImages(filteredImages);
                } else {
                    console.warn('Delete image error');
                }
            });
        },
        [images],
    );

    useEffect(() => {
        if (template || !formTemplates) return;
        if (form?.formTemplateId && formTemplates[form.formTemplateId]) {
            return setTemplate(formTemplates[form.formTemplateId]);
        }
        const formTypes = Object.keys(formTemplates)
            .filter((key) => formTemplates[key].z_campoextra3 === -1)
            .map((key) => ({
                ...formTemplates[key],
                label: formTemplates[key].description,
                value: formTemplates[key].id,
            }));
        if (formTypes.length === 1) {
            return setTemplate(formTypes[0]);
        }
        setFormTypes(formTypes);
    }, [form, formTemplates, template]);

    useEffect(() => {
        if (template && !loaded) {
            bridgeBackend
                .getContext()
                .then(({ form }) =>
                    bridgeBackend.setContext({
                        form: {
                            ...form,
                            formTemplate: template,
                        },
                    }),
                )
                .then(() =>
                    bridgeBackend.loadFragment(
                        template.guid.toUpperCase(),
                        template.url,
                        `fm-fragment-${template.guid.toUpperCase()}`,
                    ),
                )
                .then(() => setLoaded(true))
                .catch((err) => {
                    if (err.response?.data?.code === 100) {
                        console.warn('Invalid fragment GUID');
                    }
                    console.warn(err);
                    setError(true);
                });
        }
    }, [bridgeBackend, form, formTemplates, loaded, template]);

    useEffect(() => {
        if (images === null) {
            getImagesList();
        }
    }, [images, getImagesList]);

    // const onChangeFormType = useCallback((formType) => setTemplate(formType), []);

    if (!form) return null;

    if (error) {
        return (
            <div className={classes.error}>
                <Icon name="warning" className={classes.errorIcon} color={theme.colors.grey[100]} />
                <Text className={classes.errorText}>{t(`error_an_error_occurred`)}</Text>
            </div>
        );
    }

    return (
        <div className={rootClassNames}>
            {showImages && (
                <UploadImageCarousel
                    images={images}
                    limit={6}
                    onDelete={onDeleteImage}
                    onAdd={onAddImage}
                    carouselFullModalClass={carouselFullModalClassNames}
                />
            )}
            <div className={classes.contentContainer}>
                {template && (
                    <div
                        id={`fm-fragment-${template.guid.toUpperCase()}`}
                        className={classes.fragmentContainer}
                    />
                )}
                {!template && formTypes && (
                    <div className={classes.templateSelect}>
                        <Select
                            label={t('label_select_form_type')}
                            placeholder={t('label_selectone')}
                            onChange={setTemplate}
                            options={formTypes}
                            value={template}
                        />
                    </div>
                )}
                {isLoading && !formTypes && (
                    <div className={classes.loaderOverlay}>
                        <Loader className={classes.loader} />
                    </div>
                )}
            </div>
        </div>
    );
}

FormModal.propTypes = {
    form: PropTypes.object,
    showImages: PropTypes.bool,
    expandImages: PropTypes.bool,
    setExpandImages: PropTypes.func,
    isLoading: PropTypes.bool,
};

export default memo(FormModal);
